import {
  DATA_STORE,
  StoreName,
  UpdateToOnlineStatus,
} from "constants/serviceWorker";
import {
  InspectionContent,
  UpdateInspectionContent,
} from "interfaces/models/inspectionContent";
import { arrayToObject } from "utils/object";
import { InspectionItem } from "interfaces/models/inspectionItem";
import { InspectionItemResult } from "interfaces/models/inspectionItemResult";
import BaseCustomGetResponse from "./BaseCustomGetResponse";

class CustomResponseInspectionContent extends BaseCustomGetResponse {
  async getCachedInspectionContents(inspectionTypeId: string) {
    const indexedDb = await this.getIndexedDb();
    const [contents, types] = await Promise.all([
      indexedDb?.getAll(DATA_STORE, StoreName.INSPECTION_CONTENT),
      indexedDb?.getAll(DATA_STORE, StoreName.INSPECTION_TYPE),
    ]);

    /**
     * When create inspection type also add inspection content.
     * When change from online to offline, We also must get inspection content from request create inspection type
     * (inspectionContent not in payload of request create inspection type, but it was added to indexDb)
     */
    contents.unshift(...types);
    const cachedItems = contents?.filter((item) => {
      const data: UpdateInspectionContent = item.data;

      return data?.inspectionContent?.inspectionTypeId === inspectionTypeId;
    });

    return cachedItems;
  }

  /**
   *
   *  --- Flow sync data for get inspection content, item list, item result list  ---
   *  - Because when update, create, delete inspection content, item, item result
   *  - They all call the same API. (updateInspectionContent)
   *  - So when sync data, it takes indexedDB having the storeName "inspection-content"
   *  1. when add inspection type
   *  - add inspection content on top cachedItems
   *  2. when update inspection content, item, item result
   *  - update inspection content, item, item result from api get list
   *  - with indexedDB having the storeName "inspection-content"
   */
  async changeResponseData(): Promise<Response> {
    const result = await this.getResponse();
    const apiResponse = structuredClone(result.apiResponse);
    const response = result.response;
    const inspectionTypeId = this.params?.inspectionTypeId;
    if (!inspectionTypeId) {
      return await this.response({ data: apiResponse, response });
    }

    // get cachedInspectionContents
    const cachedItems = await this.getCachedInspectionContents(
      inspectionTypeId
    );

    if (!cachedItems?.length) {
      return await this.response({ data: apiResponse, response });
    }

    const isReqContent =
      this.storeNameOfRequest === StoreName.INSPECTION_CONTENT;

    const isReqItem = this.storeNameOfRequest === StoreName.INSPECTION_ITEM;

    const isReqItemResult =
      this.storeNameOfRequest === StoreName.INSPECTION_ITEM_RESULT;

    if (!Array.isArray(apiResponse?.data)) {
      apiResponse.data = [apiResponse?.data].filter((i) => !!i);
    }
    const key: keyof UpdateInspectionContent = isReqItem
      ? "contentItems"
      : "itemResults";
    const keyDeleted: keyof UpdateInspectionContent = isReqItem
      ? "deleteInspectionItemIds"
      : "deleteItemResultIds";

    for await (const cachedItem of cachedItems) {
      const cachedData: UpdateInspectionContent = cachedItem?.data;
      // case sync data for inspectionContent
      if (isReqContent) {
        apiResponse.data = (apiResponse?.data || []).map((item: any) => {
          return this.overrideCachedData({
            cachedItem:
              cachedData?.inspectionContent ||
              ({} as Partial<InspectionContent>),
            currentItem: item,
            status: cachedItem.status,
          });
        });
        if (!apiResponse?.data?.length && cachedData?.inspectionContent?.id) {
          apiResponse.data = [cachedData.inspectionContent];
        }
      }

      // case sync data for contentItems and itemResults
      if (isReqItem || isReqItemResult) {
        const deletedIds = cachedData?.[keyDeleted] || [];
        const resData: (InspectionItem | InspectionItemResult)[] =
          apiResponse?.data || [];
        const items: (InspectionItem | InspectionItemResult)[] =
          cachedData?.[key] ?? resData;
        const mapResData = arrayToObject(resData, "id");
        const mapItem = arrayToObject(items, "id");
        // update
        resData.forEach((item, index) => {
          if (mapItem[item?.id]) {
            resData[index] = { ...item, ...mapItem[item?.id] };
          }
        });
        // add item
        items.forEach((item) => {
          if (!mapResData[item.id]) {
            resData.push(item);
          }
        });
        // delete
        apiResponse.data = resData.filter((i) => !deletedIds.includes(i.id));
      }
    }

    return await this.response({ data: apiResponse, response });
  }
}

export default CustomResponseInspectionContent;
